import { extractSpecFilePath } from "@/components/ai/pending-interactive-utils";
import type { PendingInteractiveFragment } from "@/components/ai/pending-interactive-utils";
import { getPrimaryNextStepAction } from "@/components/ai/next-step-allowlist";
import type { Message } from "@/hooks/use-stream-conversation";
import { useEffect, useRef } from "react";

/**
 * Notifies the parent when a primary "Build" next-step proposal appears in the
 * conversation. Lives at this level (not inside PendingInteractiveBlock) because
 * that component unmounts/remounts on parent re-renders, killing internal timeouts.
 *
 * Retries on each messages update because specFilePath may stream in after the proposal.
 */
export function useBuildButtonNotification(
    pendingInteractiveBlock: PendingInteractiveFragment | null,
    messages: Message[],
    onBuildButtonAppear: ((specFilePath: string | null) => void) | undefined,
): void {
    const hasNotifiedTabSwitch = useRef(false);
    const hasNotifiedSpecFile = useRef(false);
    const hasPrimaryNextStep =
        pendingInteractiveBlock?.type === "nextStepsProposal" &&
        !!getPrimaryNextStepAction(pendingInteractiveBlock.actions);

    useEffect(() => {
        if (!hasPrimaryNextStep) return;

        if (!hasNotifiedTabSwitch.current) {
            hasNotifiedTabSwitch.current = true;
            const specFilePath = extractSpecFilePath(messages);
            onBuildButtonAppear?.(specFilePath);
            if (specFilePath) {
                hasNotifiedSpecFile.current = true;
            }
            return;
        }

        if (!hasNotifiedSpecFile.current) {
            const specFilePath = extractSpecFilePath(messages);
            if (specFilePath) {
                hasNotifiedSpecFile.current = true;
                onBuildButtonAppear?.(specFilePath);
            }
        }
    }, [hasPrimaryNextStep, messages, onBuildButtonAppear]);

    useEffect(() => {
        if (!hasPrimaryNextStep) {
            hasNotifiedTabSwitch.current = false;
            hasNotifiedSpecFile.current = false;
        }
    }, [hasPrimaryNextStep]);
}
